import type { ReactNode } from 'react';

const ASSET_BASE = (import.meta.env.VITE_ASSET_BASE ?? '/').replace(/\/$/, '');

/** Money is always shown as whole rands and cents, e.g. R73.00. */
export function money(amount: number) {
  return `R${amount.toFixed(2)}`;
}

/**
 * Menu images are stored as site-relative paths ("assets/opt/berry_blast.webp")
 * because they are shared with the marketing site.
 */
export function assetUrl(path: string | null) {
  if (!path) {
    return null;
  }

  return /^https?:\/\//.test(path) ? path : `${ASSET_BASE}/${path.replace(/^\//, '')}`;
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="spinner" role="status" aria-live="polite">
      <span className="spinner__ring" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function Alert({ tone = 'error', children }: { tone?: 'error' | 'info' | 'success'; children: ReactNode }) {
  return (
    <div className={`alert alert--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function EmptyState({ title, message, action }: { title: string; message: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <h2>{title}</h2>
      <p>{message}</p>
      {action}
    </div>
  );
}

/** Human-friendly label for an order status coming off the API. */
export function statusLabel(status: string) {
  switch (status) {
    case 'AwaitingPayment':
      return 'Awaiting payment';
    case 'PaymentFailed':
      return 'Payment failed';
    case 'Paid':
      return 'Paid';
    case 'Preparing':
      return 'Being made';
    case 'ReadyForCollection':
      return 'Ready for collection';
    case 'OutForDelivery':
      return 'Out for delivery';
    case 'Completed':
      return 'Completed';
    case 'Cancelled':
      return 'Cancelled';
    default:
      return status;
  }
}

export function statusTone(status: string) {
  if (status === 'Cancelled' || status === 'PaymentFailed') return 'bad';
  if (status === 'AwaitingPayment') return 'warn';
  if (status === 'Completed') return 'done';

  return 'good';
}

export function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-ZA', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
